import { deriveTitle, extractTags, forDisplay, parseFrontmatter } from '../data/markdown.ts';
import type { FromWorker, IndexDoc, ToWorker } from './protocol.ts';
import type { LocalNote } from '../data/types.ts';
import type { Folder } from '../../../shared/protocol.ts';

/**
 * Zugang zum Suchindex. Der Index ist abgeleitete Information: Fällt der Worker aus
 * (alter Browser, blockiertes WASM), fällt die Liste auf eine einfache Textsuche
 * zurück, statt gar nichts zu finden.
 */

let worker: Worker | null = null;
let ready = false;
let persistent = false;
const waiting = new Map<string, (message: FromWorker) => void>();
const readyListeners = new Set<() => void>();

export function onIndexReady(listener: () => void): () => void {
  readyListeners.add(listener);
  return () => readyListeners.delete(listener);
}

export function indexStatus(): { ready: boolean; persistent: boolean } {
  return { ready, persistent };
}

function send(message: ToWorker): void {
  worker?.postMessage(message);
}

function ask<T extends FromWorker>(build: (ref: string) => ToWorker): Promise<T | null> {
  if (!worker || !ready) return Promise.resolve(null);
  const ref = crypto.randomUUID();
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      waiting.delete(ref);
      resolve(null);
    }, 10_000);
    waiting.set(ref, (message) => {
      clearTimeout(timer);
      resolve(message as T);
    });
    send(build(ref));
  });
}

export function startSearch(): void {
  if (worker) return;
  try {
    worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
  } catch (error) {
    console.warn('[suche] Worker nicht verfügbar', error);
    return;
  }
  worker.addEventListener('message', (event: MessageEvent<FromWorker>) => {
    const message = event.data;
    if (message.t === 'ready') {
      ready = true;
      persistent = message.persistent;
      for (const listener of readyListeners) listener();
      return;
    }
    if (message.t === 'failed') {
      console.warn('[suche]', message.reason);
      return;
    }
    const resolve = waiting.get(message.ref);
    if (resolve) {
      waiting.delete(message.ref);
      resolve(message);
    }
  });
  send({ t: 'init' });
}

export function stopSearch(): void {
  send({ t: 'wipe' });
  worker?.terminate();
  worker = null;
  ready = false;
}

function toDoc(note: LocalNote, folderName: string): IndexDoc {
  const { data } = parseFrontmatter(note.content);
  return {
    id: note.id,
    title: deriveTitle(note.content),
    // Kennwörter sollen nicht über die Suche auffindbar sein, Abfragekonfiguration
    // soll die Treffer nicht verwässern.
    // Der gewonnene Text eines Dokuments ist sein Inhalt — er gehört in den Index,
    // steht aber bewusst nicht im bearbeitbaren Markdown.
    body: `${forDisplay(note.content)}\n${note.dokument?.erkannt ?? ''}`,
    tags: extractTags(note.content).join(' '),
    props: Object.entries(data)
      .map(([key, value]) => `${key.toLowerCase()}=${value.toLowerCase()}`)
      .join(' '),
    folder: folderName,
    pinned: note.pinned ? 1 : 0,
    updated: note.updatedAt,
    deleted: note.deletedAt === null ? 0 : 1,
    hasImage: /!\[[^\]]*\]\(/.test(note.content) ? 1 : 0,
    hasSecret: /```secret/.test(note.content) ? 1 : 0,
  };
}

/** Gleicht den Index mit dem lokalen Vault ab — nur was sich geändert hat. */
export async function refreshIndex(notes: LocalNote[], folders: Folder[]): Promise<void> {
  const answer = await ask<Extract<FromWorker, { t: 'stale' }>>((ref) => ({
    t: 'known',
    ref,
    notes: notes.map((note) => ({ id: note.id, updated: note.updatedAt })),
  }));
  if (!answer) return;

  if (answer.obsolete.length > 0) send({ t: 'drop', ids: answer.obsolete });
  if (answer.ids.length === 0) return;

  const names = new Map(folders.map((folder) => [folder.id, folder.name]));
  const stale = new Set(answer.ids);
  const docs = notes
    .filter((note) => stale.has(note.id))
    .map((note) => toDoc(note, note.folderId ? (names.get(note.folderId) ?? '') : ''));

  // In Häppchen, damit ein großer Erstabgleich den Worker nicht minutenlang blockiert.
  for (let start = 0; start < docs.length; start += 200) {
    send({ t: 'put', docs: docs.slice(start, start + 200) });
  }
}

export async function searchIds(
  text: string,
  limit = 200,
): Promise<{ ids: string[]; fuzzy: string[] } | null> {
  const answer = await ask<Extract<FromWorker, { t: 'result' }>>((ref) => ({
    t: 'query',
    ref,
    text,
    limit,
  }));
  return answer ? { ids: answer.ids, fuzzy: answer.fuzzy } : null;
}
